import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { VaPagination } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { useFeatureToggle } from '~/platform/utilities/feature-toggles';
import {
  buildDateFormatter,
  getShowEightPhases,
  isChampvaProviderClaim,
} from '~/platform/shared/claims-appeals';

import { generateTrackedItems } from '../../utils/trackedItemProcessing';
import BasicLink from '../web-component-wrappers/BasicLink';
import { ITEMS_PER_PAGE, TRACKED_ITEM_STATUSES } from '../../constants';
import { getPhaseItemText } from '../../utils/helpers';
import * as TrackedItem from '../../utils/trackedItemContent';
import TimezoneDiscrepancyMessage from '../TimezoneDiscrepancyMessage';

export default function RecentActivity({ claim, closedTrackedItemsEnabled }) {
  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const cstClaimPhasesEnabled = useToggleValue(TOGGLE_NAMES.cstClaimPhases);
  const claimAttributes = claim?.attributes || {};
  const showEightPhases = getShowEightPhases(
    claimAttributes.claimTypeCode,
    cstClaimPhasesEnabled,
  );

  const { NEEDED_FROM_OTHERS, NEEDED_FROM_YOU } = TRACKED_ITEM_STATUSES;

  const getPhaseItemDescription = (currentPhaseBack, phase) => {
    const phaseItemText = getPhaseItemText(phase, showEightPhases);
    const movedIntoText = `Your claim moved into ${phaseItemText}`;
    const movedBackText = `Your claim moved back to ${phaseItemText}`;
    switch (phase) {
      case 1:
      case 8:
        if (showEightPhases) return phaseItemText;
        return movedIntoText;
      case 2:
      case 7:
        return movedIntoText;
      case 3:
      case 4:
      case 5:
      case 6:
        if (currentPhaseBack) return movedBackText;
        return movedIntoText;
      default:
        return '';
    }
  };

  const generatePhaseItems = () => {
    const claimPhaseDates = claimAttributes.claimPhaseDates || {};
    const { currentPhaseBack = false } = claimPhaseDates;
    const previousPhases =
      claimPhaseDates.previousPhases &&
      typeof claimPhaseDates.previousPhases === 'object'
        ? claimPhaseDates.previousPhases
        : {};
    const claimPhases = [];

    const regex = /\d+/;

    // Add phase dates using the complete date of other phases
    const phaseKeys = Object.keys(previousPhases);
    phaseKeys.forEach(phaseKey => {
      const phase = Number(phaseKey.match(regex)[0]) + 1;
      claimPhases.push({
        id: `phase_${phase}`,
        type: 'phase_entered',
        // We are assuming here that each phaseKey is of the format:
        // phaseXCompleteDate, where X is some integer between 1 and 7
        // NOTE: Adding 1 because the a claimPhases completion date is
        // analagous to the phase after it's start date
        // eg. phase1CompleteDate = start date for phase 2
        phase,
        description: getPhaseItemDescription(currentPhaseBack, phase),
        date: previousPhases[phaseKey],
      });
    });

    // Add initial phase date since its not inculded in previousPhases
    claimPhases.push({
      id: 'phase_1',
      type: 'filed',
      phase: 1,
      description: getPhaseItemDescription(currentPhaseBack, 1),
      date: claimAttributes.claimDate,
    });

    // When cst_claim_phases is enabled and is a disability compensation claim
    // then we remove steps 4-6 and only keep step 3
    return showEightPhases
      ? claimPhases
      : claimPhases.filter(
          claimPhase => claimPhase.phase <= 3 || claimPhase.phase >= 7,
        );
  };

  const generateChampvaPhaseItems = () => {
    const { status, claimDate, closeDate, claimStatusMeta } = claimAttributes;
    const statusMap = claimStatusMeta?.whatWeAreDoing?.statusMap || {};
    const items = [];

    // Push decided entry first so that ties on date sort it above received
    if (closeDate) {
      const decidedEntry = statusMap[status];
      if (decidedEntry) {
        items.push({
          id: 'champva_step_2',
          type: 'phase_entered',
          description: `Your application moved to ${
            decidedEntry.recentActivityTitle || decidedEntry.title
          }.`,
          date: closeDate,
        });
      }
    }

    const receivedEntry = statusMap.pending;
    if (receivedEntry && claimDate) {
      items.push({
        id: 'champva_step_1',
        type: 'phase_entered',
        description: `Your application moved to ${
          receivedEntry.recentActivityTitle || receivedEntry.title
        }.`,
        date: claimDate,
      });
    }

    return items;
  };

  const getSortedItems = () => {
    // Get items from trackedItems and claimPhaseDates
    const trackedItems = generateTrackedItems(
      claim,
      closedTrackedItemsEnabled,
      TrackedItem,
    );

    const phaseItems =
      isChampvaProviderClaim(claim) && claimAttributes.claimStatusMeta?.overview
        ? generateChampvaPhaseItems()
        : generatePhaseItems();
    const items = [...trackedItems, ...phaseItems];

    return items.sort((item1, item2) => {
      return new Date(item2.date) - new Date(item1.date);
    });
  };

  const [currentPage, setCurrentPage] = useState(1);
  const items = getSortedItems();
  const pageLength = items.length;
  const numPages = Math.ceil(pageLength / ITEMS_PER_PAGE);
  const shouldPaginate = numPages > 1;

  const hasRequestType = itemStatus => {
    return itemStatus === NEEDED_FROM_OTHERS || itemStatus === NEEDED_FROM_YOU;
  };

  const requestType = item => {
    if (item.status === NEEDED_FROM_YOU) {
      return 'Request for you';
    }

    return null;
  };

  let currentPageItems = items;

  if (shouldPaginate) {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = Math.min(currentPage * ITEMS_PER_PAGE, pageLength);
    currentPageItems = items.slice(start, end);
  }

  const onPageSelect = useCallback(
    selectedPage => {
      setCurrentPage(selectedPage);
    },
    [setCurrentPage],
  );

  const thirdPartyRequestText = item => {
    return (
      <div data-testid={`item-from-others-${item.id}`}>
        <p className="vads-u-margin-top--0 vads-u-margin-bottom--0p5">
          {item.activityDescription
            ? item.activityDescription
            : TrackedItem.renderDefaultThirdPartyMessage(item.oldDisplayName)}
        </p>
        {!closedTrackedItemsEnabled && (
          <BasicLink
            disable-analytics
            label={`Learn more about this notice for ${
              item.friendlyName || item.displayName
            }`}
            path={`/your-claims/${claim.id}/needed-from-others/${item.id}`}
            text="Learn more about this notice"
          />
        )}
      </div>
    );
  };

  return (
    <div className="recent-activity-container">
      <h3 className="vads-u-margin-top--0">Recent activity</h3>
      {/* File submission in progress doesn't apply to CHAMPVA, so the upload
          cutoff note has nothing to describe here. Scoped rather than removed
          because the other caller, FileSubmissionsInProgress, still needs it.
          Keyed on the provider rather than cstChampvaCustomContent on purpose:
          the note is wrong for CHAMPVA whichever content variant is rendering,
          so it stays hidden even with the flag off. */}
      {!isChampvaProviderClaim(claim) && <TimezoneDiscrepancyMessage />}
      {pageLength > 0 && (
        <ol className="va-list-horizontal">
          {currentPageItems.map(item => (
            <li
              key={item.id}
              className="vads-u-margin-bottom--2 vads-u-padding-bottom--1"
            >
              {!item.hideDate && (
                <h4 className="vads-u-margin-y--0">
                  {item.displayDate || buildDateFormatter()(item.date)}
                </h4>
              )}
              {hasRequestType(item.status) ? (
                <>
                  {item.status === NEEDED_FROM_OTHERS ? (
                    <div className="vads-u-margin-y--0p5">
                      <span className="usa-label">Requested for you</span>
                    </div>
                  ) : (
                    requestType(item) && (
                      <p className="vads-u-margin-top--0p5 vads-u-margin-bottom--0">
                        {requestType(item)}
                      </p>
                    )
                  )}
                  <p
                    className="item-description vads-u-margin-top--0 vads-u-margin-bottom--1"
                    data-dd-privacy="mask"
                    data-dd-action-name="item description"
                  >
                    {item.description}
                  </p>
                </>
              ) : (
                <p
                  className="item-description vads-u-margin-top--0p5 vads-u-margin-bottom--1"
                  data-dd-privacy="mask"
                  data-dd-action-name="item description"
                >
                  {item.description}
                </p>
              )}
              {item.status === NEEDED_FROM_OTHERS &&
                thirdPartyRequestText(item)}
              {item.requestPageLink && (
                <BasicLink
                  disable-analytics
                  path={item.requestPageLink.href}
                  label={item.requestPageLink.label}
                  text="Review the request"
                />
              )}
            </li>
          ))}
        </ol>
      )}
      {shouldPaginate && (
        <VaPagination
          className="vads-u-border--0"
          page={currentPage}
          pages={numPages}
          onPageSelect={e => onPageSelect(e.detail.page)}
        />
      )}
    </div>
  );
}

RecentActivity.propTypes = {
  claim: PropTypes.object,
  closedTrackedItemsEnabled: PropTypes.bool,
};
